import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X, Check } from 'lucide-react';

export default function CalendarFilterModal({
  isOpen,
  onClose,
  type = 'stops', // 'stops' or 'missions' or 'expedition'
  title: customTitle,
  subtitle: customSubtitle,
  currentFilter = { startDate: '', endDate: '', isAll: false },
  onApplyFilter
}) {
  const today = new Date();
  const formatIsoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const todayStr = formatIsoDate(today);

  const [viewYear, setViewYear] = useState(() => {
    if (currentFilter.startDate) {
      const parts = currentFilter.startDate.split('-');
      if (parts.length === 3) return parseInt(parts[0], 10);
    }
    return today.getFullYear();
  });

  const [viewMonth, setViewMonth] = useState(() => {
    if (currentFilter.startDate) {
      const parts = currentFilter.startDate.split('-');
      if (parts.length === 3) return parseInt(parts[1], 10) - 1;
    }
    return today.getMonth();
  });

  // Selection states:
  const [selectedStart, setSelectedStart] = useState(currentFilter.startDate || todayStr);
  const [selectedEnd, setSelectedEnd] = useState(currentFilter.endDate || todayStr);
  const [isAllSelected, setIsAllSelected] = useState(!!currentFilter.isAll);
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  // Calendar counts state:
  const [calendarCounts, setCalendarCounts] = useState({ stops: {}, missions: {} });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  // Sync with currentFilter when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedStart(currentFilter.startDate || todayStr);
      setSelectedEnd(currentFilter.endDate || todayStr);
      setIsAllSelected(!!currentFilter.isAll);
      setIsSelectingRange(false);
      
      if (currentFilter.startDate) {
        const parts = currentFilter.startDate.split('-');
        if (parts.length === 3) {
          setViewYear(parseInt(parts[0], 10));
          setViewMonth(parseInt(parts[1], 10) - 1);
        }
      }
    }
  }, [isOpen, currentFilter]);

  // Fetch counts when viewYear or viewMonth changes
  useEffect(() => {
    if (!isOpen) return;
    const fetchCounts = async () => {
      setIsLoadingCounts(true);
      try {
        const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
        const res = await fetch(`/api/database/calendar-counts?month=${monthStr}`);
        if (res.ok) {
          const data = await res.json();
          setCalendarCounts({
            stops: data.stops || {},
            missions: data.missions || {}
          });
        }
      } catch (err) {
        console.error('Error loading calendar counts:', err);
      }
      setIsLoadingCounts(false);
    };

    fetchCounts();
  }, [isOpen, viewYear, viewMonth]);

  if (!isOpen) return null;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const weekDayHeaders = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(y => y - 1);
    } else {
      setViewMonth(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(y => y + 1);
    } else {
      setViewMonth(m => m + 1);
    }
  };

  const handleGoToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedStart(todayStr);
    setSelectedEnd(todayStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
  };

  // Build calendar matrix
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
  const totalDays = lastDayOfMonth.getDate();

  // Day of week: 0 is Sun, 1 is Mon. We want 0=Mon, ..., 6=Sun
  let startDayOfWeek = firstDayOfMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const daysGrid = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    daysGrid.push(null);
  }
  for (let day = 1; day <= totalDays; day++) {
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    daysGrid.push({
      day,
      dateStr: dStr
    });
  }

  const countsMap = type === 'missions' ? calendarCounts.missions : calendarCounts.stops;

  // Handle Day Click
  const handleDayClick = (dateStr) => {
    setIsAllSelected(false);
    if (!isSelectingRange) {
      // First click: sets start date, begins range selection mode
      setSelectedStart(dateStr);
      setSelectedEnd(dateStr);
      setIsSelectingRange(true);
    } else {
      // Second click: completes range selection
      if (dateStr < selectedStart) {
        setSelectedEnd(selectedStart);
        setSelectedStart(dateStr);
      } else {
        setSelectedEnd(dateStr);
      }
      setIsSelectingRange(false);
    }
  };

  // Handle Day Double Click (instant apply single date)
  const handleDayDoubleClick = (dateStr) => {
    setIsAllSelected(false);
    setSelectedStart(dateStr);
    setSelectedEnd(dateStr);
    setIsSelectingRange(false);
    if (onApplyFilter) {
      onApplyFilter({
        startDate: dateStr,
        endDate: dateStr,
        isAll: false
      });
    }
    onClose();
  };

  // Quick Preset Handlers
  const handlePresetToday = () => {
    setSelectedStart(todayStr);
    setSelectedEnd(todayStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const handlePresetThisWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = (day === 0 ? -6 : 1) - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diffToMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);

    const monStr = formatIsoDate(mon);
    const sunStr = formatIsoDate(sun);

    setSelectedStart(monStr);
    setSelectedEnd(sunStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setViewYear(mon.getFullYear());
    setViewMonth(mon.getMonth());
  };

  const handlePresetThisMonth = () => {
    const now = new Date();
    const startM = new Date(now.getFullYear(), now.getMonth(), 1);
    const endM = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setSelectedStart(formatIsoDate(startM));
    setSelectedEnd(formatIsoDate(endM));
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  };

  const handlePresetAll = () => {
    setIsAllSelected(true);
    setIsSelectingRange(false);
  };

  const handleApply = () => {
    if (isAllSelected) {
      onApplyFilter({ startDate: null, endDate: null, isAll: true });
    } else {
      onApplyFilter({
        startDate: selectedStart || todayStr,
        endDate: selectedEnd || selectedStart || todayStr,
        isAll: false
      });
    }
    onClose();
  };

  // Check if date is within selected range
  const isDateSelected = (dateStr) => {
    if (isAllSelected) return false;
    if (isSelectingRange && hoverDate) {
      const minD = selectedStart < hoverDate ? selectedStart : hoverDate;
      const maxD = selectedStart < hoverDate ? hoverDate : selectedStart;
      return dateStr >= minD && dateStr <= maxD;
    }
    const minD = selectedStart <= selectedEnd ? selectedStart : selectedEnd;
    const maxD = selectedStart <= selectedEnd ? selectedEnd : selectedStart;
    return dateStr >= minD && dateStr <= maxD;
  };

  const isRangeStart = (dateStr) => !isAllSelected && dateStr === selectedStart;
  const isRangeEnd = (dateStr) => !isAllSelected && dateStr === selectedEnd;

  // Format selection summary
  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(10, 15, 29, 0.82)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '16px',
          width: '560px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 25px rgba(56, 189, 248, 0.15)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: type === 'missions' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                border: `1px solid ${type === 'missions' ? '#f59e0b' : '#38bdf8'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: type === 'missions' ? '#f59e0b' : '#38bdf8'
              }}
            >
              <Calendar size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '800', color: '#f8fafc' }}>
                {customTitle || (type === 'expedition' ? "Date d'expédition" : (type === 'missions' ? 'Filtrer les Missions par date' : 'Filtrer les Stops par date'))}
              </h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                {customSubtitle || (type === 'expedition' ? 'Sélectionnez un jour ou une plage de dates de livraison' : 'Sélectionnez un jour ou une plage de dates pour afficher les données')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* PRESET SHORTCUTS BAR */}
        <div
          style={{
            padding: '10px 20px',
            background: '#090d16',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}
        >
          <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '700', marginRight: '4px' }}>
            Raccourcis :
          </span>
          <button
            type="button"
            onClick={handlePresetToday}
            style={{
              background: !isAllSelected && selectedStart === todayStr && selectedEnd === todayStr ? '#38bdf8' : '#1e293b',
              color: !isAllSelected && selectedStart === todayStr && selectedEnd === todayStr ? '#0f172a' : '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={handlePresetThisWeek}
            style={{
              background: '#1e293b',
              color: '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Cette semaine
          </button>
          <button
            type="button"
            onClick={handlePresetThisMonth}
            style={{
              background: '#1e293b',
              color: '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Ce mois
          </button>
          <button
            type="button"
            onClick={handlePresetAll}
            style={{
              background: isAllSelected ? '#22c55e' : '#1e293b',
              color: isAllSelected ? '#0f172a' : '#cbd5e1',
              border: `1px solid ${isAllSelected ? '#22c55e' : '#334155'}`,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            Tout afficher (Sans filtre)
          </button>
        </div>

        {/* CALENDAR BODY */}
        <div style={{ padding: '16px 20px', flex: 1, overflowY: 'auto' }}>
          {/* Month / Year Navigator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.05rem', fontWeight: '800', color: '#f8fafc' }}>
                {monthNames[viewMonth]} {viewYear}
              </span>
              {isLoadingCounts && (
                <span style={{ fontSize: '0.72rem', color: '#38bdf8' }}>Chargement...</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                onClick={handleGoToToday}
                style={{
                  background: 'rgba(56, 189, 248, 0.1)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Mois courant
              </button>
              <button
                type="button"
                onClick={handlePrevMonth}
                style={{
                  background: '#1e293b',
                  color: '#f8fafc',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                style={{
                  background: '#1e293b',
                  color: '#f8fafc',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Weekday Headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '6px',
              marginBottom: '8px',
              textAlign: 'center'
            }}
          >
            {weekDayHeaders.map((header, idx) => (
              <div
                key={idx}
                style={{
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  color: idx >= 5 ? '#ef4444' : '#64748b',
                  padding: '4px 0'
                }}
              >
                {header}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '6px'
            }}
          >
            {daysGrid.map((item, idx) => {
              if (!item) {
                return (
                  <div
                    key={`empty-${idx}`}
                    style={{
                      height: '62px',
                      background: 'rgba(15, 23, 42, 0.4)',
                      borderRadius: '8px'
                    }}
                  />
                );
              }

              const { day, dateStr } = item;
              const count = countsMap[dateStr] || 0;
              const isToday = dateStr === todayStr;
              const isSelected = isDateSelected(dateStr);
              const isStart = isRangeStart(dateStr);
              const isEnd = isRangeEnd(dateStr);

              let bg = '#1e293b';
              let borderColor = '#334155';
              let textColor = '#f8fafc';

              if (isSelected) {
                bg = type === 'missions' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(56, 189, 248, 0.25)';
                borderColor = type === 'missions' ? '#f59e0b' : '#38bdf8';
              }
              if (isStart || isEnd) {
                bg = type === 'missions' ? '#f59e0b' : '#38bdf8';
                borderColor = type === 'missions' ? '#d97706' : '#0284c7';
                textColor = '#0f172a';
              }

              return (
                <div
                  key={dateStr}
                  onClick={() => handleDayClick(dateStr)}
                  onDoubleClick={() => handleDayDoubleClick(dateStr)}
                  onMouseEnter={() => isSelectingRange && setHoverDate(dateStr)}
                  title="Cliquer pour sélectionner une plage, ou double-cliquer pour choisir ce jour immédiatement"
                  style={{
                    height: '62px',
                    background: bg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    padding: '5px 6px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    transition: 'all 0.15s ease',
                    boxShadow: isStart || isEnd ? '0 0 10px rgba(56, 189, 248, 0.4)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: isToday || isSelected ? '800' : '600',
                        color: textColor
                      }}
                    >
                      {day}
                    </span>

                    {isToday && (
                      <span
                        style={{
                          fontSize: '0.6rem',
                          background: isStart || isEnd ? '#0f172a' : '#38bdf8',
                          color: isStart || isEnd ? '#38bdf8' : '#0f172a',
                          padding: '1px 4px',
                          borderRadius: '4px',
                          fontWeight: '800'
                        }}
                      >
                        Auj.
                      </span>
                    )}
                  </div>

                  {/* Day Count Badge */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {count > 0 ? (
                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: '800',
                          padding: '2px 6px',
                          borderRadius: '6px',
                          background: isStart || isEnd
                            ? 'rgba(15, 23, 42, 0.3)'
                            : (type === 'missions' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)'),
                          color: isStart || isEnd
                            ? '#0f172a'
                            : (type === 'missions' ? '#f59e0b' : '#22c55e'),
                          border: isStart || isEnd
                            ? 'none'
                            : `1px solid ${type === 'missions' ? 'rgba(245, 158, 11, 0.4)' : 'rgba(34, 197, 94, 0.4)'}`,
                          display: 'inline-block',
                          textAlign: 'center',
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {count} {type === 'missions' ? 'm.' : 'st.'}
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: '0.65rem',
                          color: isStart || isEnd ? 'rgba(15, 23, 42, 0.6)' : '#475569',
                          fontWeight: '500'
                        }}
                      >
                        0 {type === 'missions' ? 'mis.' : 'stop'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #1e293b',
            background: '#090d16',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '700' }}>
              Filtre sélectionné :
            </span>
            <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#38bdf8' }}>
              {isAllSelected ? (
                'Tout afficher (aucun filtre de date)'
              ) : selectedStart === selectedEnd ? (
                `Journée du ${formatReadableDate(selectedStart)}`
              ) : (
                `Du ${formatReadableDate(selectedStart)} au ${formatReadableDate(selectedEnd)}`
              )}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#1e293b',
                color: '#94a3b8',
                border: '1px solid #334155',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={handleApply}
              style={{
                background: type === 'missions' ? '#f59e0b' : '#22c55e',
                color: '#0f172a',
                border: 'none',
                padding: '8px 18px',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: type === 'missions' ? '0 0 12px rgba(245, 158, 11, 0.3)' : '0 0 12px rgba(34, 197, 94, 0.3)'
              }}
            >
              <Check size={15} strokeWidth={3} />
              <span>Appliquer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
